import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { createResourceLocatorString } from '../../../util/routes';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { RegNumInput } from '../../WP/RegNumInput';
import css from './WheelplaceSearchCard.module.css';

// Brands mirrored from the prototype (AnnonserPage.jsx brandOptions).
// These provide an instant UI while the /makes endpoint warms up; once the
// API resolves we replace the list with the live set (which includes slugs
// the model/series endpoints need).
const FALLBACK_BRANDS = [
  'Aiways', 'Alfa Romeo', 'Audi', 'BMW', 'BYD', 'Chevrolet', 'Citroën', 'Cupra',
  'Dacia', 'DS', 'Fiat', 'Ford', 'Honda', 'Hyundai', 'Jaguar', 'Jeep', 'Kia',
  'Lamborghini', 'Land Rover', 'Lexus', 'Lynk & Co', 'Maserati', 'Mazda',
  'Mercedes', 'MG', 'Mini', 'Mitsubishi', 'Nissan', 'Opel', 'Peugeot',
  'Polestar', 'Porsche', 'Renault', 'Seat', 'Skoda', 'Smart', 'Subaru',
  'Suzuki', 'Tesla', 'Toyota', 'Volkswagen', 'Volvo', 'Xpeng',
];

const slugify = v =>
  String(v)
    .toLowerCase()
    .replace(/å/g, 'a')
    .replace(/ä/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/é/g, 'e')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const API_BASE = process.env.REACT_APP_API_URL || '';
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 30 }, (_, i) => CURRENT_YEAR - i);

const WheelplaceSearchCard = ({ initialKeywords = '' }) => {
  const [keywords, setKeywords] = useState(initialKeywords);
  const [regNum, setRegNum] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [form, setForm] = useState({
    brandSlug: '',
    brandName: '',
    modelSlug: '',
    modelName: '',
    year: '',
    seriesSlug: '',
    seriesName: '',
  });

  // Live brand list (fallback until the API resolves)
  const [brands, setBrands] = useState(
    FALLBACK_BRANDS.map(name => ({ slug: slugify(name), name, fallback: true }))
  );
  const [models, setModels] = useState([]);
  const [seriesList, setSeriesList] = useState([]);

  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingSeries, setLoadingSeries] = useState(false);

  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  // Fetch brands once
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/proxy/wheelsize/makes`)
      .then(r => (r.ok ? r.json() : Promise.reject(r)))
      .then(data => {
        if (cancelled || !Array.isArray(data)) return;
        const filtered = data
          .filter(b => !b.regions || b.regions.includes('eudm'))
          .map(b => ({ slug: b.slug, name: b.name_en || b.name || b.slug }))
          .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
        if (filtered.length > 0) setBrands(filtered);
      })
      .catch(() => {
        /* keep fallback list */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch models when brand changes
  useEffect(() => {
    if (!form.brandSlug) {
      setModels([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingModels(true);
    fetch(`${API_BASE}/api/proxy/wheelsize/models?make=${encodeURIComponent(form.brandSlug)}`)
      .then(r => (r.ok ? r.json() : Promise.reject(r)))
      .then(data => {
        if (cancelled || !Array.isArray(data)) return;
        setModels(
          data.map(m => ({ slug: m.slug, name: m.name_en || m.name || m.slug }))
        );
      })
      .catch(err => {
        console.error('Failed to fetch models', err);
        if (!cancelled) setModels([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingModels(false);
      });
    return () => {
      cancelled = true;
    };
  }, [form.brandSlug]);

  // Fetch series when brand + model + year are all set
  useEffect(() => {
    if (!form.brandSlug || !form.modelSlug || !form.year) {
      setSeriesList([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingSeries(true);
    fetch(
      `${API_BASE}/api/proxy/wheelsize/series?make=${encodeURIComponent(form.brandSlug)}&model=${encodeURIComponent(form.modelSlug)}&year=${encodeURIComponent(form.year)}`
    )
      .then(r => (r.ok ? r.json() : Promise.reject(r)))
      .then(data => {
        if (cancelled || !Array.isArray(data)) return;
        setSeriesList(
          data.map(s => ({ slug: s.slug, name: s.name || s.name_en || s.slug }))
        );
      })
      .catch(err => {
        console.error('Failed to fetch series', err);
        if (!cancelled) setSeriesList([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSeries(false);
      });
    return () => {
      cancelled = true;
    };
  }, [form.brandSlug, form.modelSlug, form.year]);

  const pushSearch = params => {
    const path = createResourceLocatorString('SearchPage', routeConfiguration, {}, params);
    history.push(path);
  };

  const handleKeywordSubmit = e => {
    e.preventDefault();
    const q = keywords.trim();
    if (!q) return;
    pushSearch({ keywords: q });
  };

  const handleAdvancedSubmit = e => {
    e.preventDefault();
    const params = {};
    if (form.brandName) params.pub_categoryLevel1 = form.brandName;
    if (form.modelName) params.pub_categoryLevel2 = form.modelName;
    if (form.year) params.pub_year = form.year;
    if (form.seriesName) params.pub_series = form.seriesName;
    if (keywords.trim()) params.keywords = keywords.trim();
    pushSearch(params);
  };

  const onBrandChange = e => {
    const slug = e.target.value;
    const brand = brands.find(b => b.slug === slug);
    setForm({
      brandSlug: slug,
      brandName: brand?.name || '',
      modelSlug: '',
      modelName: '',
      year: '',
      seriesSlug: '',
      seriesName: '',
    });
  };

  const onModelChange = e => {
    const slug = e.target.value;
    const model = models.find(m => m.slug === slug);
    setForm(prev => ({
      ...prev,
      modelSlug: slug,
      modelName: model?.name || '',
      year: '',
      seriesSlug: '',
      seriesName: '',
    }));
  };

  const onYearChange = e => {
    const year = e.target.value;
    setForm(prev => ({
      ...prev,
      year,
      seriesSlug: '',
      seriesName: '',
    }));
  };

  const onSeriesChange = e => {
    const slug = e.target.value;
    const series = seriesList.find(s => s.slug === slug);
    setForm(prev => ({
      ...prev,
      seriesSlug: slug,
      seriesName: series?.name || '',
    }));
  };

  const modelPlaceholder = loadingModels ? 'Laddar…' : 'Modell';
  const seriesPlaceholder = loadingSeries ? 'Laddar…' : 'Serie';

  return (
    <div className={css.card}>
      <form className={css.keywordForm} onSubmit={handleKeywordSubmit}>
        <input
          type="text"
          className={css.keywordInput}
          placeholder="Sök annonser..."
          value={keywords}
          onChange={e => setKeywords(e.target.value)}
          aria-label="Sök annonser"
        />
        <button type="submit" className={css.keywordSubmit} aria-label="Sök">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>
      </form>

      {/* RegNumInput moved to the filter sidebar (CarInfoSidebar
        * block) so the car-specific lookup sits with "your car's
        * specs" rather than next to the generic text search. */}

      <button
        type="button"
        className={css.advancedToggle}
        onClick={() => setAdvancedOpen(prev => !prev)}
        aria-expanded={advancedOpen}
      >
        <span className={css.advancedToggleSign}>{advancedOpen ? '—' : '+'}</span>
        {advancedOpen ? 'Dölj avancerad sökning' : 'Avancerad sökning'}
      </button>

      {/* Always rendered so the container can animate open/closed.
        * Height is driven by grid-template-rows: 0fr / 1fr; inner
        * form fades + slides in after the container starts
        * expanding. Content is aria-hidden when collapsed so
        * screen readers skip over it. */}
      <div
        className={classNames(css.advancedWrap, { [css.advancedWrapOpen]: advancedOpen })}
        aria-hidden={!advancedOpen}
      >
        <form className={css.advancedFields} onSubmit={handleAdvancedSubmit}>
          <select
            className={css.advancedSelect}
            value={form.brandSlug}
            onChange={onBrandChange}
            aria-label="Märke"
            disabled={!advancedOpen}
          >
            <option value="">Märke</option>
            {brands.map(b => (
              <option key={b.slug} value={b.slug}>
                {b.name}
              </option>
            ))}
          </select>

          <select
            className={css.advancedSelect}
            value={form.modelSlug}
            onChange={onModelChange}
            aria-label="Modell"
            disabled={!form.brandSlug || loadingModels}
          >
            <option value="">{modelPlaceholder}</option>
            {models.map(m => (
              <option key={m.slug} value={m.slug}>
                {m.name}
              </option>
            ))}
          </select>

          <select
            className={css.advancedSelect}
            value={form.year}
            onChange={onYearChange}
            aria-label="Årsmodell"
            disabled={!form.modelSlug}
          >
            <option value="">Årsmodell</option>
            {YEAR_OPTIONS.map(y => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>

          <select
            className={css.advancedSelect}
            value={form.seriesSlug}
            onChange={onSeriesChange}
            aria-label="Serie"
            disabled={!form.year || loadingSeries}
          >
            <option value="">{seriesPlaceholder}</option>
            {seriesList.map(s => (
              <option key={s.slug} value={s.slug}>
                {s.name}
              </option>
            ))}
          </select>

          <button type="submit" className={css.advancedSubmit} disabled={!advancedOpen}>
            Sök
          </button>
        </form>
      </div>
    </div>
  );
};

export default WheelplaceSearchCard;
